package com.fantasytenis.shared.data.contests.repository

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.contests.api.ContestsApi
import com.fantasytenis.shared.data.contests.dto.*
import com.fantasytenis.shared.domain.bracket.entity.LockOverride
import com.fantasytenis.shared.domain.contests.entity.*
import com.fantasytenis.shared.domain.tennis.entity.PlayerCosts
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import kotlinx.serialization.json.Json

/**
 * Contests Repository Interface.
 */
interface ContestsRepository {
    suspend fun getContest(contestId: String): Result<ContestEntity>
    suspend fun listContests(
        status: Int? = null,
        type: Int? = null,
        slateId: String? = null,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<ContestEntity>>
    suspend fun enterContest(contestId: String): Result<EntryEntity>
    suspend fun cancelEntry(entryId: String): Result<EntryEntity>
    suspend fun getUserEntries(contestId: String? = null): Result<List<EntryEntity>>
    suspend fun getContestLeaderboard(
        contestId: String,
        limit: Int = 50,
        offset: Int = 0
    ): Result<List<EntryEntity>>

    suspend fun getSlate(slateId: String): Result<SlateDto>
    suspend fun getSlatePlayers(slateId: String, gender: String): Result<List<TennisPlayerEntity>>
}

/**
 * Contests Repository Implementation.
 */
class ContestsRepositoryImpl(
    private val contestsApi: ContestsApi
) : ContestsRepository {

    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun getContest(contestId: String): Result<ContestEntity> {
        return when (val response = contestsApi.getContest(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun listContests(
        status: Int?,
        type: Int?,
        slateId: String?,
        limit: Int,
        offset: Int
    ): Result<List<ContestEntity>> {
        return when (val response = contestsApi.listContests(status, type, slateId, limit, offset)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun enterContest(contestId: String): Result<EntryEntity> {
        return when (val response = contestsApi.enterContest(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun cancelEntry(entryId: String): Result<EntryEntity> {
        return when (val response = contestsApi.cancelEntry(entryId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getUserEntries(contestId: String?): Result<List<EntryEntity>> {
        return when (val response = contestsApi.getUserEntries(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getContestLeaderboard(
        contestId: String,
        limit: Int,
        offset: Int
    ): Result<List<EntryEntity>> {
        return when (val response = contestsApi.getContestLeaderboard(contestId, limit, offset)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getSlate(slateId: String): Result<SlateDto> {
        return when (val response = contestsApi.getSlate(slateId)) {
            is ApiResponse.Success -> Result.success(response.data)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getSlatePlayers(slateId: String, gender: String): Result<List<TennisPlayerEntity>> {
        return when (val response = contestsApi.getSlatePlayers(slateId)) {
            is ApiResponse.Success -> Result.success(
                response.data.map { it.toEntity(gender) }
            )
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    // ===== DTO TO ENTITY MAPPERS =====

    private fun SlatePlayerDto.toEntity(gender: String): TennisPlayerEntity {
        return TennisPlayerEntity(
            id = playerId,
            externalId = playerId,
            name = name,
            firstName = null,
            lastName = null,
            country = nationality ?: "",
            atpRanking = ranking,
            eloRating = 1500.0,
            gender = gender,
            costs = PlayerCosts(
                serve = serveCost,
                forehand = forehandCost,
                backhand = backhandCost,
                volleys = volleysCost,
                fighting = fightingCost
            ),
            isActive = isActive,
            avatarUrl = null,
            // Match context fields
            matchId = matchId,
            opponentId = opponentId,
            opponentName = opponentName,
            opponentRanking = opponentRanking,
            matchTime = matchTime
        )
    }

    private fun ContestDto.toEntity(): ContestEntity {
        val parsedPayoutStructure = payoutStructure?.let {
            try {
                val tiers = json.decodeFromString<List<PayoutTierDto>>(it)
                tiers.map { tier ->
                    PrizeLevel(
                        minRank = tier.minRank,
                        maxRank = tier.maxRank,
                        prize = (tier.amount ?: 0) / 100.0
                    )
                }
            } catch (e: Exception) {
                emptyList()
            }
        } ?: emptyList()

        return ContestEntity(
            id = id,
            name = name,
            tournamentId = slateId,
            tournamentName = "", // Would come from slate lookup
            startTime = parseTimestamp(startTime),
            rosterLockTime = parseTimestamp(lineupLockTime),
            lineupLockTime = lineupLockTime,
            entryFee = entryFee / 100.0,
            prizePool = prizePool / 100.0,
            maxEntries = maxEntries,
            currentEntries = currentEntries,
            status = ContestStatus.entries.getOrElse(status - 1) { ContestStatus.UPCOMING },
            contestType = ContestType.entries.getOrElse(type - 1) { ContestType.GPP },
            salaryCap = salaryCap,
            rosterSize = 5, // Default
            prizeStructure = parsedPayoutStructure,
            isGuaranteed = isGuaranteed,
            isFeatured = isFeatured,
            lockOverride = LockOverride.fromApiValue(lockOverride),
            lockOverrideBy = lockOverrideBy.ifBlank { null },
            lockOverrideAt = lockOverrideAt.ifBlank { null },
            lockOverrideReason = lockOverrideReason.ifBlank { null },
            perfectLineup = perfectLineup?.toEntity()
        )
    }

    private fun PerfectLineupDto.toEntity(): PerfectLineupEntity {
        return PerfectLineupEntity(
            serve = serve.toEntity(),
            forehand = forehand.toEntity(),
            backhand = backhand.toEntity(),
            volleys = volleys.toEntity(),
            fighting = fighting.toEntity(),
            totalCost = totalCost,
            totalPoints = totalPoints,
            calculatedAt = calculatedAt
        )
    }

    private fun PerfectLineupSlotDto.toEntity(): PerfectLineupSlotEntity {
        return PerfectLineupSlotEntity(
            playerId = playerId,
            playerName = playerName,
            cost = cost,
            points = points
        )
    }

    private fun ContestEntryDto.toEntity(): EntryEntity {
        return EntryEntity(
            id = id,
            contestId = contestId,
            userId = userId,
            lineupId = lineupId,
            entryNumber = 1,
            totalPoints = totalPoints,
            rank = rank,
            payoutAmount = payoutAmount / 100.0,
            status = when (status) {
                1 -> EntryStatus.PENDING
                2 -> EntryStatus.CONFIRMED
                3 -> EntryStatus.LIVE
                4 -> EntryStatus.FINISHED
                5 -> EntryStatus.CANCELLED
                6 -> EntryStatus.PAID
                else -> EntryStatus.PENDING
            },
            createdAt = parseTimestamp(createdAt),
            // User profile fields from enriched API response
            displayName = displayName ?: userName,
            username = username,
            avatarUrl = avatarUrl
        )
    }

    private fun parseTimestamp(isoString: String): Long {
        return try {
            // Simple ISO parsing - in real app would use proper datetime library
            0L
        } catch (e: Exception) {
            0L
        }
    }
}
